/**
 * InboxWebSocket — cliente WS nativo contra o Worker (issue #209).
 *
 * Substitui o socket.io-client durante a POC. Conecta em
 * `<WIDGET_BASE_URL>/agents/inbox/:tenantId/ws`. O backend posta mensagens com
 * POST assinado no DO InboxRoom, que faz fanout para todas as abas abertas.
 *
 * Autenticação: a cada (re)conexão pede um token curto (5 min) em
 * `GET /api/inbox/ws-token` e conecta em `?token=`. O Worker recusa o WebSocket
 * sem token válido para o tenant. O JWT do usuário nunca vai na URL.
 *
 * Opt-in: ativado pela chave `ingrave.inbox.wsViaDo=true` no localStorage (ou
 * build-time via `import.meta.env.VITE_INBOX_WS_VIA_DO`). Enquanto a flag
 * estiver desligada, o frontend continua usando socket.io-client.
 */

import { inboxApi } from './api';

type Listener = (payload: { event: string; data: unknown; at?: number }) => void;

const optIn = (): boolean => {
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('ingrave.inbox.wsViaDo') === 'true') return true;
  } catch { /* SSR/no localStorage */ }
  try {
    // @ts-expect-error import.meta.env existe no build Vite
    if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_INBOX_WS_VIA_DO === 'true') return true;
  } catch { /* noop */ }
  return false;
};

const baseUrl = (): string => {
  try {
    // @ts-expect-error import.meta.env existe no build Vite
    const fromEnv = typeof import.meta !== 'undefined' ? import.meta.env?.VITE_AGENTS_BASE_URL : undefined;
    if (typeof fromEnv === 'string' && fromEnv) return fromEnv.replace(/\/+$/, '');
  } catch { /* noop */ }
  return 'wss://disparo.ingrave.com.br';
};

export const inboxWsEnabled = optIn;

export interface InboxWsTicket { token: string; tenantId?: string; url?: string | null }

/** Padrão: token curto emitido pelo backend para a organização ativa */
export const fetchInboxWsTicket = async (): Promise<InboxWsTicket> => {
  const r = await inboxApi.wsToken();
  return { token: r.token, tenantId: r.tenantId, url: r.url };
};

export interface InboxWebSocketOptions {
  tenantId: string;
  /** Busca um token novo a cada conexão (o token vence em minutos). Default: GET /api/inbox/ws-token */
  getTicket?: () => Promise<InboxWsTicket>;
  /** URL base do Worker (sem trailing slash). Default: wss://disparo.ingrave.com.br */
  baseUrl?: string;
  /** Backoff inicial; default 1s, dobrando até 30s. */
  reconnectDelayMs?: number;
}

export class InboxWebSocket {
  private base: string;
  private socket: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private stateListeners = new Set<(connected: boolean) => void>();
  private connected = false;
  private retry = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private destroyed = false;
  private connecting = false;

  constructor(private opts: InboxWebSocketOptions) {
    this.base = (opts.baseUrl ?? baseUrl()).replace(/\/+$/, '');
  }

  /** URL do WS: a que o backend devolveu (mesma base do broadcast) ou a base local */
  private wsUrl(ticket: InboxWsTicket): string {
    const tenantId = ticket.tenantId || this.opts.tenantId;
    const fallback = `${this.base.replace(/^http/, 'ws')}/agents/inbox/${encodeURIComponent(tenantId)}/ws`;
    return `${ticket.url || fallback}?token=${encodeURIComponent(ticket.token)}`;
  }

  connect(): void {
    void this.open();
  }

  private async open(): Promise<void> {
    if (this.destroyed || this.connecting || (this.socket && this.socket.readyState <= WebSocket.OPEN)) return;
    this.connecting = true;
    let url: string;
    try {
      url = this.wsUrl(await (this.opts.getTicket ?? fetchInboxWsTicket)());
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[inboxWs] sem token para o WS (vai tentar de novo):', err);
      this.connecting = false;
      this.scheduleReconnect();
      return;
    }
    this.connecting = false;
    if (this.destroyed) return;
    try {
      const ws = new WebSocket(url);
      this.socket = ws;
      ws.addEventListener('open', () => {
        this.retry = 0;
        this.setConnected(true);
        this.startPing();
      });
      ws.addEventListener('message', (ev) => this.handleMessage(ev));
      ws.addEventListener('close', () => {
        this.stopPing();
        this.setConnected(false);
        if (!this.destroyed) this.scheduleReconnect();
      });
      ws.addEventListener('error', () => {
        // eslint-disable-next-line no-console
        console.warn('[inboxWs] erro no WS (vai reconectar)');
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[inboxWs] falha ao criar WS:', err);
      this.scheduleReconnect();
    }
  }

  private handleMessage(ev: MessageEvent): void {
    if (typeof ev.data !== 'string') return;
    try {
      const parsed = JSON.parse(ev.data) as { type?: string; event?: string; data?: unknown; at?: number; tenantId?: string };
      if (parsed.type === 'pong') return; // keepalive
      const payload = { event: parsed.event ?? 'message', data: parsed.data, at: parsed.at };
      for (const l of this.listeners) l(payload);
    } catch {
      // silencioso: payload não-JSON
    }
  }

  private scheduleReconnect(): void {
    if (this.destroyed) return;
    const base = this.opts.reconnectDelayMs ?? 1000;
    const delay = Math.min(30_000, base * Math.pow(2, this.retry));
    this.retry += 1;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = setTimeout(() => this.connect(), delay);
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        try { this.socket.send(JSON.stringify({ type: 'ping', at: Date.now() })); } catch { /* noop */ }
      }
    }, 25_000);
  }
  private stopPing(): void {
    if (this.pingTimer) { clearInterval(this.pingTimer); this.pingTimer = null; }
  }

  private setConnected(v: boolean): void {
    if (this.connected === v) return;
    this.connected = v;
    for (const l of this.stateListeners) l(v);
  }

  on(listener: Listener): () => void { this.listeners.add(listener); return () => this.listeners.delete(listener); }
  onState(listener: (connected: boolean) => void): () => void { this.stateListeners.add(listener); listener(this.connected); return () => this.stateListeners.delete(listener); }

  close(): void {
    this.destroyed = true;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.stopPing();
    try { this.socket?.close(1000, 'bye'); } catch { /* noop */ }
    this.socket = null;
    this.setConnected(false);
  }
}